import { logEvent, logRBIEvent, updateUniversalAttributes } from 'features/analytics';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
} from 'features/analytics/constants';
import { registerScreenContextProvider } from 'features/analytics/core/screen-context';
import { IClickEvent } from 'features/analytics/types';
import logger from 'features/observability/logger';
import { updateAmplitudeUserAttributes } from 'features/profile/utils/analytics';

import { IRestaurant } from '@rbi-ctg/store';
import { getStaticPathname, getStaticReferrerPathname } from 'navigation/location/location';
import { routes } from 'navigation/routing';
import { convertQueryStringToObject } from 'navigation/utils';
import { IStaticPageRoute } from 'remote/queries/static-page';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

export interface IUtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

interface ILogPageView {
  pathname: string;
  store: IRestaurant;
  distanceToStoreMiles?: string;
}

interface ILogButtonClickEvent {
  type?: IClickEvent['type'];
  attributes: Partial<IClickEvent['attributes']> & {
    component: ClickEventComponentNames;
    name: string;
    [key: string]: string | number | undefined | boolean | any[] | Record<string, any>;
  };
}

let logPageViewParameters: ILogPageView | null | undefined = null;
let staticRoutes: string[] = [];

export const registerNavigationScreenContext = () =>
  registerScreenContextProvider(() => ({
    currentScreen: getStaticPathname(),
    previousScreen: getStaticReferrerPathname(),
  }));

export const logNavigationClick = (eventName: CustomEventNames, attrs = {}, customFlags = {}) => {
  logger.debug(`[amplitude][logNavigationClick]: ${eventName}`);
  logEvent(
    CustomEventNames.BUTTON_CLICK,
    EventTypes.Navigation,
    { Name: eventName, ...attrs },
    customFlags
  );
};

const pageViewAttributes = (
  pathname: string,
  store: IRestaurant,
  distanceToStoreMiles?: string,
  query?: string,
  includeQuery = false
) => ({
  path: pathname,
  restaurantId: store?.number ?? '',
  restaurantAddress: store?.physicalAddress?.address1 ?? '',
  restaurantZip: store?.physicalAddress?.postalCode ?? '',
  restaurantCity: store?.physicalAddress?.city ?? '',
  restaurantState: store?.physicalAddress?.stateProvince ?? '',
  restaurantCountry: store?.physicalAddress?.country ?? '',
  'Google.Page': '',
  'Google.DocumentReferrer': '',
  pathname,
  sanityId: '',
  referrer: '',
  ...(includeQuery ? { query } : {}),
  distanceToStoreMiles,
});

/** Logs a page view once route information is available. */
export const logPageView = (
  pathname: string,
  store: IRestaurant,
  query?: string,
  distanceToStoreMiles?: string
) => {
  const isBaseRoute = routes.base === pathname;
  const isLocalRoute =
    isBaseRoute ||
    Object.values(routes).some(route => route !== '/' && pathname.startsWith(route as any));

  if (!isLocalRoute && !staticRoutes.length) {
    logPageViewParameters = { pathname, store, distanceToStoreMiles };
    return;
  }

  logRBIEvent({
    name: CustomEventNames.PAGE_VIEW,
    type: EventTypes.Other,
    attributes: pageViewAttributes(pathname, store, distanceToStoreMiles, query, true),
  });
};

export const updateStaticRoutes = (newStaticRoutes: IStaticPageRoute[]) => {
  staticRoutes = newStaticRoutes.reduce((acc: string[], route) => {
    const staticPath =
      route?.localePath?.[syncStorage.getString(SyncStorageKey.LANGUAGE) ?? '']?.current ||
      route?.path?.current;
    if (staticPath) {
      acc.push(`/${staticPath}`);
    }
    return acc;
  }, []);

  if (staticRoutes.length && logPageViewParameters) {
    const { pathname, store, distanceToStoreMiles } = logPageViewParameters;
    logRBIEvent({
      name: CustomEventNames.PAGE_VIEW,
      type: EventTypes.Other,
      attributes: pageViewAttributes(pathname, store, distanceToStoreMiles),
    });
    logPageViewParameters = undefined;
  }
};

/** Logs a generic navigation or interface button click. */
export const logButtonClick = ({ attributes }: ILogButtonClickEvent) => {
  logRBIEvent({
    name: CustomEventNames.BUTTON_CLICK,
    type: EventTypes.Other,
    attributes,
  });
};

/**
 * Updates UTM Attributes in the universal attributes.
 * Used For attribution tracking.
 */
export const setUTMParamsFromUrl = (url: string) => {
  const {
    utm_source = '',
    utm_campaign = '',
    utm_medium = '',
    utm_content = '',
    utm_term = '',
  } = convertQueryStringToObject<IUtmParams & Record<string, unknown>>(url);

  if (utm_source || utm_campaign || utm_medium || utm_content || utm_term) {
    const attributes = {
      'UTM Source': utm_source,
      'UTM Medium': utm_medium,
      'UTM Campaign': utm_campaign,
      'UTM Term': utm_term,
      'UTM Content': utm_content,
    };
    updateUniversalAttributes(attributes);
    updateAmplitudeUserAttributes(attributes);
  }
};

/**
 * Updates UTM Attributes in the universal attributes.
 * Used For attribution tracking.
 */
export const setUTMParams = (utmParams: any) => {
  const {
    utm_source = '',
    utm_campaign = '',
    utm_medium = '',
    utm_content = '',
    utm_term = '',
  } = utmParams;

  if (utm_source || utm_campaign || utm_medium || utm_content || utm_term) {
    const attributes = {
      'UTM Source': utm_source,
      'UTM Medium': utm_medium,
      'UTM Campaign': utm_campaign,
      'UTM Term': utm_term,
      'UTM Content': utm_content,
    };
    updateUniversalAttributes(attributes);
    updateAmplitudeUserAttributes(attributes);
  }
};
